import React, { useContext, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import "../../styles/form/FormPage.scss";
import {
  fetchSignedUrl,
  saveToBucket,
  submitForm,
} from "../../api/apiManagement";
import { PageContext } from "../Pages"; // ⚠️ path 按你實際結構確認

const MAX_FILES = 3;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const SUPPORTED_FORMATS = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "application/pdf",
];

const schema = yup.object({
  fullName: yup.string().trim().required("請輸入香港身份證全名"),

  mobile: yup
    .string()
    .required("請輸入香港手提電話號碼")
    .matches(/^(5|6|7|8|9)\d{7}$/, "請輸入有效的香港手提電話號碼"),

  purchaseChannel: yup.string().required("請選擇購買渠道"),

  totalAmount: yup
    .number()
    .transform((value, originalValue) =>
      originalValue === "" ? undefined : value,
    )
    .typeError("請輸入有效金額")
    .positive("金額必須大於 0")
    .required("請輸入所有收據的總金額"),

  receipts: yup
    .mixed()
    .test("required", "請至少上載 1 張收據", (value) => {
      return value && value.length > 0;
    })
    .test("maxFiles", "最多只可上載 3 張收據", (value) => {
      return !value || value.length <= MAX_FILES;
    })
    .test("fileSize", "每個檔案不可超過 10MB", (value) => {
      if (!value || value.length === 0) return true;
      return Array.from(value).every((file) => file.size <= MAX_FILE_SIZE);
    })
    .test("fileFormat", "只支援 JPG / JPEG / PNG / PDF", (value) => {
      if (!value || value.length === 0) return true;
      return Array.from(value).every((file) =>
        SUPPORTED_FORMATS.includes(file.type),
      );
    }),

  tnc: yup.boolean().oneOf([true], "請先同意條款及細則"),
});

const FormPage = () => {
  const fileInputRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showTncPopup, setShowTncPopup] = useState(false);

  const { setCurrentStep } = useContext(PageContext); // 如未需要可先移除

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      fullName: "",
      mobile: "",
      purchaseChannel: "",
      totalAmount: "",
      receipts: [],
      tnc: false,
    },
  });

  const receipts = watch("receipts") || [];

  const uploadAllFiles = async (files) => {
    const photoNames = [];

    for (const file of files) {
      const res = await fetchSignedUrl();
      const { signedUrl, fileName, headers } = res.data;

      await saveToBucket(signedUrl, file, {
        contentType: file.type,
        extraHeaders: headers,
      });

      photoNames.push(fileName);
    }

    return photoNames;
  };

  const onSubmit = async (d) => {
    if (isSubmitting) return;

    console.log("form data:", d);

    const files = Array.from(d.receipts || []);

    if (!files.length) return;

    setIsSubmitting(true);

    try {
      const photoNames = await uploadAllFiles(files);

      const finalData = {
        fullName: d.fullName,
        phone: d.mobile,
        channel: d.purchaseChannel,
        amount: Number(d.totalAmount),
        tnc: d.tnc,
        photoNames,
      };

      console.log("submit payload:", finalData);

      const submitRes = await submitForm(finalData);
      console.log("Form submitted:", submitRes.data);

      setCurrentStep(3);
    } catch (err) {
      console.error("Upload flow error:", err);
      console.log("response error:", err?.response?.data);
      alert("提交失敗，請稍後再試。" + err?.response?.data.errors[0].message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    const currentFiles = Array.from(receipts || []);
    const mergedFiles = [...currentFiles, ...selectedFiles].slice(0, MAX_FILES);

    setValue("receipts", mergedFiles, {
      shouldValidate: true,
      shouldDirty: true,
    });

    e.target.value = "";
  };

  const handleRemoveFile = (indexToRemove) => {
    const updatedFiles = Array.from(receipts).filter(
      (_, index) => index !== indexToRemove,
    );

    setValue("receipts", updatedFiles, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  return (
    <div className="form-page-container">
      <img
        className="background-image"
        src="/images/form_page_background.jpg"
        alt="background"
      />

      <div className="form-container">
        <div className="form-title">填寫資料</div>

        <div className="form-description">
          請確保以下填寫的資料準確無誤，上載的收據清晰顯示所有資料。每張收據只限登記一次，上載及登記單據的次數不限。如果顧客持有超過一張收據，可以一次過上傳所有相關收據作累積金額之用。{" "}
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="form-group">
            <label>* 香港身份證全名</label>
            <input type="text" {...register("fullName")} />
            <p className="error-text">{errors.fullName?.message || ""}</p>
          </div>

          <div className="form-group">
            <label>* 香港手提電話號碼</label>
            <p className="small-text">
              (可接收及發送WhatsApp訊息，用作公布結果。)
            </p>
            <input type="tel" maxLength={8} {...register("mobile")} />
            <p className="error-text">{errors.mobile?.message || ""}</p>
          </div>

          <div className="form-group">
            <label>* 購買渠道</label>
            <select {...register("purchaseChannel")}>
              <option value="">請選擇</option>
              <option value="萬寧門市及網店">萬寧門市及網店</option>
              <option value="百佳超級市場">百佳超級市場</option>
              <option value="FUSION">FUSION</option>
              <option value="TASTE">TASTE</option>
              <option value="TASTE x FRESH 新鮮生活">
                TASTE x FRESH 新鮮生活{" "}
              </option>
              <option value="INTERNATIONAL">INTERNATIONAL </option>
              <option value="FOOD PARC">FOOD PARC </option>
              <option value="GREAT FOOD HALL">GREAT FOOD HALL </option>
              <option value="PNS 網購">PNS 網購 </option>
              <option value="屈臣氏門市及網店">屈臣氏門市及網店 </option>
              <option value="惠康超級市場門市及網店">
                惠康超級市場門市及網店{" "}
              </option>
              <option value="Market Place">Market Place </option>
              <option value="3hreesixty">3hreesixty </option>
              <option value="Oliver’s The Delicatessen">
                Oliver’s The Delicatessen{" "}
              </option>
              <option value="HKTVmall">HKTVmall </option>
              <option value="pandamart">pandamart </option>
              <option value="永旺（香港）百貨有限公司">
                永旺（香港）百貨有限公司{" "}
              </option>
              <option value="一田超市">一田超市 </option>
              <option value="UNY 生活創庫">UNY 生活創庫 </option>
              <option value="APITA">APITA </option>
              <option value="多個渠道">多個渠道 </option>
            </select>
            <p className="error-text">
              {errors.purchaseChannel?.message || ""}
            </p>
          </div>

          <div className="form-group">
            <label>* 所有收據的總金額</label>
            <input type="number" step="0.01" {...register("totalAmount")} />
            <p className="error-text">{errors.totalAmount?.message || ""}</p>
          </div>

          <div className="form-group">
            <label>
              * 上載收據並保留所有收據正本
              <br />
            </label>

            <p className="small-text">
              (必須為指定商戶消費收據，並確保相片內容清晰)
              <br />
              請以清晰 PNG / JPEG / PDF 上載，檔案大小最少1MB
            </p>

            <input
              ref={fileInputRef}
              type="file"
              accept=".png,.jpg,.jpeg,.pdf"
              multiple
              style={{ display: "none" }}
              onChange={handleFileChange}
            />

            <div className="upload-image-container">
              <img
                className={`upload-image-button ${
                  receipts.length >= MAX_FILES || isSubmitting ? "disabled" : ""
                }`}
                onClick={() => {
                  if (receipts.length < MAX_FILES && !isSubmitting) {
                    fileInputRef.current?.click();
                  }
                }}
                src="/images/upload_image_button.png"
                alt="upload_image_button"
              />

              {receipts.length > 0 && (
                <div className="receipts-outline-container">
                  {receipts.map((file, index) => (
                    <div
                      className="receipts-container"
                      key={`${file.name}-${index}`}
                    >
                      <div className="receipts-text">圖片{index + 1}</div>

                      <button
                        type="button"
                        className="remove-receipt-button"
                        onClick={() => handleRemoveFile(index)}
                        disabled={isSubmitting}
                      >
                        x
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <p className="error-text">{errors.receipts?.message || ""}</p>
          </div>

          <div className="form-group">
            <label>* 必需填寫</label>
            <p className="small-text checkbox-text">
              <input type="checkbox" {...register("tnc")} />
              本人已閱讀並同意高露潔 x 洪嘉豪「一日店長」齒科級亮白研究院
              <span className="tnc-link" onClick={() => setShowTncPopup(true)}>
                活動條款及細則
              </span>
              。
            </p>
            <p className="error-text">{errors.tnc?.message || ""}</p>
          </div>

          {/* <div className="form-group">
            <button type="submit" disabled={isSubmitting}>
              <img src="./images/submit_button.png" alt="submit_button" />
            </button>
          </div> */}
          <div className="form-group">
            <button
              type="submit"
              disabled={isSubmitting}
              className="submit-btn"
              onSubmit={handleSubmit(onSubmit)}
            >
              <img
                src="./images/submit_button.png"
                alt="submit_button"
                className={
                  isSubmitting ? "submit-button loading" : "submit-button"
                }
              />
            </button>
          </div>
        </form>
      </div>
      {showTncPopup && (
        <div className="tnc-overlay" onClick={() => setShowTncPopup(false)}>
          <div className="tnc-popup" onClick={(e) => e.stopPropagation()}>
            <div className="tnc-popup-inner">
              <div
                className="tnc-close-button"
                onClick={() => setShowTncPopup(false)}
              >
                x
              </div>
              <div className="tnc-title">
                高露潔 x 洪嘉豪
                <br />
                「一日店長」齒科級亮白研究院
                <br />
                推廣活動條款及細則
              </div>
              <div className="tnc-description">
                高露潔 x 洪嘉豪「一日店長」齒科級亮白研究院推廣活動
                之參加者須受以下條款及細則約束。參加本活動即代表你已閱讀、
                明白及同意受本活動條款及細則約束。
              </div>
              <div></div>
              <div className="tnc-content">
                <ol>
                  <li>
                    本活動只接受於參加本活動時可接收及發送 WhatsApp 訊息及已接受
                    WhatsApp
                    應用程式內的服務條款及隠私政策的香港手提電話號碼登記。如香港手提電話號碼於參加本推廣活動時尚未登記
                    WhatsApp 帳戶，或 WhatsApp 帳戶尚未接受 WhatsApp
                    應用程式內的服務條款及隠私政策，將無法接收確認活動資格的
                    WhatsApp 訊息，確認活動資格的訊息亦不設補發。
                  </li>

                  <li>
                    活動推廣及登記期：2026年3月23日0時0分至2026年4月12日23時59分，共1輪及50個名額。
                    <br />
                    高露潔 x
                    洪嘉豪「一日店長」齒科級亮白研究院推廣活動日、兌換活動資格日期：2026年4月18日下午。
                    <br />
                    高露潔 x 洪嘉豪「一日店長」齒科級亮白研究院推廣活動地點：HOW
                    to live
                    well（地址：尖沙咀河內道18號K11購物藝術館2樓201-01號舖）。
                    <br />
                    活動詳情：於 HOW to live well 兌換一杯高露潔 x HOW to live
                    well 咖啡特飲，並可與歌手洪嘉豪於指定活動時間見面。
                  </li>

                  <li>
                    每位顧客不限於系統內上載及登記單據的次數，惟參加本活動及兌換活動資格次數只有一次，並以顧客的姓名、手提電話號碼和有關活動通知及入場卷的
                    WhatsApp
                    訊息作準。顧客可集合所有、購至不同商戶的單據以作登記。任何重複或錯誤輸入資料，即告作廢。
                  </li>

                  <li>
                    於推廣期內（2026年3月23日0時0分至2026年4月12日23時59分），顧客於香港地區的萬寧門市及網店、百佳超級市場、FUSION、TASTE、TASTE
                    x FRESH 新鮮生活、INTERNATIONAL、FOOD PARC、GREAT FOOD
                    HALL、PNS
                    網購、屈臣氏門市及網店、惠康超級市場門市及網店、Market
                    Place、3hreesixty、Oliver’s The
                    Delicatessen、HKTVmall、pandamart、永旺（香港）百貨有限公司、一田超市、UNY
                    生活創庫、APITA
                    購買高露潔光感白白金持久美白牙膏、高露潔光感白白金迅白美白牙膏、高露潔光感白閃耀亮白牙膏或高露潔光感白臻極亮白牙膏，憑有效購物收據於推廣網站
                    https://www.colgatehkowdentistcampaign.com
                    登記，並輸入正確資料（香港身份證全名、可接收及發送 WhatsApp
                    訊息的香港手提電話號碼、購買渠道、所有收據的總金額）及上載所有收據圖片即可參與。參加者須保留所有已登記的收據正本，並於活動前出示已作網上登記、最高購物金額的收據正本，以作確認活動資格／核實之用。
                  </li>

                  <li>
                    用作登記累積金額的有效收據必須為
                    2026年3月23日0時0分至2026年4月12日23時59分期間由香港地區的萬寧門市及網店、百佳超級市場、FUSION、TASTE、TASTE
                    x FRESH 新鮮生活、INTERNATIONAL、FOOD PARC、GREAT FOOD
                    HALL、PNS
                    網購、屈臣氏門市及網店、惠康超級市場門市及網店、Market
                    Place、3hreesixty、Oliver’s The
                    Delicatessen、HKTVmall、pandamart、永旺（香港）百貨有限公司、一田超市、UNY
                    生活創庫、APITA
                    發出的收據正本。收據正本必須清楚列明所購買之產品名稱、產品數量、購物金額、零售商、收據日期及收據編號。產品包括高露潔光感白白金持久美白牙膏、高露潔光感白白金迅白美白牙膏、高露潔光感白閃耀亮白牙膏或高露潔光感白臻極亮白牙膏。每張收據只限登記一次，任何重複或錯誤輸入資料，即告作廢。
                  </li>

                  <li>
                    於推廣期內（2026年3月23日0時0分至2026年4月12日23時59分）購買高露潔光感白白金持久美白牙膏、高露潔光感白白金迅白美白牙膏、高露潔光感白閃耀亮白牙膏或高露潔光感白臻極亮白牙膏，累積金額最高之50名顧客可獲2026年4月18日活動名額一個。如有相同的最高累積金額，則以收據上的消費日期和時間為先。結果將於2026年4月14日於高露潔的
                    Facebook 及 Instagram 專頁公布。
                  </li>

                  <li>
                    每張收據購買高露潔光感白白金持久美白牙膏、高露潔光感白白金迅白美白牙膏、高露潔光感白閃耀亮白牙膏或高露潔光感白臻極亮白牙膏，均可獲一次登記機會。不論單次購買數量多少，每張收據只限登記一次。
                  </li>

                  <li>本活動之一切時間以推廣網站伺服器的時間為準。</li>

                  <li>
                    累積金額最高之50名顧客將於公佈結果後3個工作天內收到專人透過
                    WhatsApp
                    發送的活動通知及入場卷，獲得活動資格的顧客需於2026年4月17日前以
                    WhatsApp
                    訊息確認出席與否。如未能於指定時間內回覆出席則視為放棄活動資格，活動不設有候補名單，顧客不得異議。若因個人相關資料不完整，導致無法收到活動通知者，視同放棄活動資格。若資料有不實、不正確或不完整，香港高露潔棕欖有限公司保留取消活動資格的權利。
                  </li>

                  <li>
                    參加活動當日，顧客須與工作人員確認香港身份證全名和已登記的香港電話號碼，並出示已作網上登記、最高購物金額的收據正本和
                    WhatsApp 發送的活動入場卷及通知（恕不接受 WhatsApp
                    截圖）。如顧客未能於活動開始前出示所有有關資料，香港高露潔棕欖有限公司將取消其活動資格，而不作另行通知。在法律允許的最大範圍內，主辦單位對於活動登記之登記資料及文件有最終之審核權及詮釋權。
                  </li>

                  <li>
                    本活動不接受送貨單、複製收據、優惠券、信用卡客戶存根、免費換領或領取產品或已退貨的產品之收據。
                  </li>

                  <li>
                    如顧客於登記收據時確認購買高露潔光感白白金持久美白牙膏、高露潔光感白白金迅白美白牙膏、高露潔光感白閃耀亮白牙膏或高露潔光感白臻極亮白牙膏，但經核對後沒有購買高露潔光感白白金持久美白牙膏、高露潔光感白白金迅白美白牙膏、高露潔光感白閃耀亮白牙膏或高露潔光感白臻極亮白牙膏，其得獎資格將被取消，而不作另行通知。
                  </li>

                  <li>
                    所有獎品均不可轉讓、轉售或兌換現金。顧客須同意遵守活動供應商所列有關活動上之各項條款及細則。香港高露潔棕欖有限公司及活動供應商對所有參加活動之後果概不負責。
                  </li>

                  <li>
                    在適用法律允許的最大範圍內，主辦單位及活動供應商就任何因參加本活動而引起或與之相關的間接、附帶、特殊或懲罰性損失不承擔責任。此等條款不排除或限制法律不能排除或限制之責任。
                  </li>

                  <li>
                    收據如有任何過期、不完整、偽造、影印、損毀、重印、修改或塗污，均作廢論，香港高露潔棕欖有限公司有權取消其得獎資格。
                  </li>

                  <li>
                    系統及技術：主辦單位香港高露潔棕欖有限公司將盡力維持系統穩定；惟如因電腦、網絡、通訊或技術問題導致資料遺失、錯誤、無法辨識或損毀，在法律允許的最大範圍內，主辦單位不承擔相關責任。
                  </li>

                  <li>
                    參加者所輸入的資料將不可更改，所有資料均以網上登記之記錄為準，任何重覆、錯誤或不完整的資料將被視作無效。若活動所填寫之資料不實或不正確，導致顧客無法收到活動通知或詳情，香港高露潔棕欖有限公司恕不負責。
                  </li>

                  <li>
                    本活動只接受已接受 WhatsApp 應用程式內的服務條款及私隱政策的
                    WhatsApp
                    帳戶及具有由香港流動電話網絡供應商提供的傳統語音服務的香港手提電話號碼登記。如手提電話號碼於登記本活動時尚未接受
                    WhatsApp 應用程式內的服務條款及私隱政策、沒有上述的 WhatsApp
                    帳戶或語音服務，或於登記本活動／領獎時登記電話號碼已停止服務、無效、已更改、無法發送／接收
                    WhatsApp
                    訊息或無法打出／接聽傳統語音通話，其活動資格將被取消。
                  </li>

                  <li>
                    如需查詢登記或活動資料，參加者或獲得活動資格者本人必須以登記手提電話以
                    WhatsApp 訊息作聯絡，否則將不獲受理。
                  </li>

                  <li>
                    如有任何爭議，在法律允許的最大範圍內，香港高露潔棕欖有限公司保留最終解釋權和決定權。如香港高露潔棕欖有限公司發現參加者使用或教唆他人使用不正當或欺詐手法干擾推廣活動的運作、破壞或影響活動的舉辦、誠信、公平或順利進行，香港高露潔棕欖有限公司有權終止參加者的參加資格、把已登記的資料作廢及限制參加者登入推廣網站和／或向參加者提供相關服務，並保留向有關人士追究的權利。
                  </li>

                  <li>
                    推廣活動查詢電話：+852 5239 9531（週一至週五，例假日除外
                    10:00-13:00，14:00-18:00）。本查詢電話不接受任何來電查詢，只用作傳送和接收
                    WhatsApp 訊息用途。
                  </li>

                  <li>
                    除上述注意事項，如有未盡事宜，在法律許可的最大範圍內，主辦單位擁有修改和調整及合理取消本活動的權利，亦有權對本活動之所有事宜作出解釋。任何變更內容及其他詳細注意事項，則公告於活動網站。
                  </li>

                  <li>
                    為了讓您能夠參與此活動及聯絡，香港高露潔棕欖有限公司會收集、處理以及使用您所提供的相關個人資料（如姓名、電話、電子郵件地址等）。您的個人資料只用於參加該活動。主辦單位及委託的第三方會根據高露潔隱私權聲明和香港法律法規收集、處理、使用及跨境傳輸您的個人資料。報名參加活動即表示您同意這些條款（同意主辦單位及委託的第三方按前述目的收集、使用及處理本人提供之個人資料），並同意高露潔隱私權聲明。香港高露潔棕欖有限公司會確保所收集、傳送、儲存及使用的個人資料將遵照香港特別行政區法例第486章《個人資料（私隱）條例》的規定處理。除本條款及細則另有約定或參加者另行書面同意或授權外，參加者的資料只會用於是次活動作身份確認及退款用途，活動結束後3個月內，相關資料會被刪除。參加者有權根據《個人資料（私隱）條例》向本公司提出查閱及改正其個人資料之要求。正式查詢、查閱或改正要求，請以
                    WhatsApp（+852 5239 9531）與我們聯絡。
                  </li>

                  <li>
                    參加者同意是次推廣活動及此條款及細則須受香港法律所管限並按其詮釋。香港法院對任何爭議具有專有審判權。
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FormPage;
